<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AiAgentTemplatesAPI from 'dashboard/api/aiAgents/templates';
import AiAgentForm from './AiAgentForm.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('aiAgents/getUIFlags');
const initialAgent = ref(null);
const isLoadingTemplate = ref(false);

const goToList = () => {
  router.push({ name: 'ai_agents' });
};

const createAgent = async payload => {
  const agent = await store.dispatch('aiAgents/create', payload);
  if (!agent) return null;

  useAlert(t('AI_AGENTS.API.CREATE_SUCCESS'));
  return agent;
};

const save = async payload => {
  const agent = await createAgent(payload);
  if (agent) goToList();
};

const publish = async payload => {
  const agent = await createAgent(payload);
  if (!agent) return;

  await store.dispatch('aiAgents/publish', agent.id);
  useAlert(t('AI_AGENTS.API.PUBLISH_SUCCESS'));
  goToList();
};

const loadTemplate = async () => {
  const templateKey = route.query.templateSlug || route.query.templateId;
  if (!templateKey) return;

  try {
    isLoadingTemplate.value = true;
    const { data } = await AiAgentTemplatesAPI.show(templateKey);
    initialAgent.value = {
      emoji: data.emoji,
      name: data.name,
      description: data.description,
      instructions: data.instructions,
      provider: data.provider,
      model: data.model,
      temperature: data.temperature,
      handoff_message: data.handoff_message,
      resolution_message: data.resolution_message,
      guardrails: data.guardrails || [],
      response_guidelines: data.response_guidelines || [],
      actions: data.actions || [],
    };
  } catch {
    useAlert(t('AI_AGENTS.TEMPLATES_PAGE.LOAD_ERROR'));
    router.replace({ name: 'ai_agents_new' });
  } finally {
    isLoadingTemplate.value = false;
  }
};

onMounted(loadTemplate);

const isSaving = computed(() => uiFlags.value.isCreating);
const isPublishing = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isPublishing
);
</script>

<template>
  <div
    v-if="isLoadingTemplate"
    class="flex flex-1 items-center justify-center p-8 text-sm text-n-slate-11"
  >
    {{ t('AI_AGENTS.TEMPLATES_PAGE.LOADING') }}
  </div>
  <AiAgentForm
    v-else
    :title="t('AI_AGENTS.FORM.CREATE_TITLE')"
    :agent="initialAgent || {}"
    :is-saving="isSaving"
    :is-publishing="isPublishing"
    @save="save"
    @publish="publish"
    @cancel="goToList"
  />
</template>
